/**
 * TikTok, Snap and LinkedIn Ads for the agency's clients (read-only). Built only on what the ads backend supports for
 * these networks today (/v1/ads-{network}/agency, flag connectors.agency_networks, active client grant with
 * read:reports):
 *  - Connections: each client's own ad accounts on the network, with status and last sync. The client connects the
 *    account from its own workspace; the backend has no agency-level credential or account-to-client mapping for
 *    these networks, so neither is offered here.
 *  - Reports: per-client spend and performance (totals per currency, campaigns, freshness, partial-data dates).
 * No campaign creation, budget change or other write.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Heading, Panel, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { useResource } from '../kit/resource';
import { Badge, DataTable, Field, Grid, Loadable, Money, Notice, Screen, Stat, Tabs, When } from '../kit/ui';
import { adsNetworksApi, type AgencyNetworkClients, type AgencyNetworkProvider, type AgencyNetworkReport } from '../api/ads-networks';
import { CONNECTION_TONE } from '../lib/logic';

type Client = AgencyNetworkClients['clients'][number];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const day = (d: Date) => d.toISOString().slice(0, 10);

/** Default report range: the last 30 days up to today (UTC). */
export function defaultWindow(now: Date): { from: string; to: string } {
  return { from: day(new Date(now.getTime() - 29 * 86_400_000)), to: day(now) };
}

function useNetworksApi() {
  const { http } = useSurface();
  return useMemo(() => adsNetworksApi(http), [http]);
}

function useNumber() {
  const { locale } = useSurface();
  return useMemo(() => {
    const f = new Intl.NumberFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', { maximumFractionDigits: 2 });
    return (n: number | null | undefined) => (n === null || n === undefined ? '-' : f.format(n));
  }, [locale]);
}

/** Connection status of every granted client on one network. */
export function NetworkConnections({ provider, data, onReport }: { provider: AgencyNetworkProvider; data: AgencyNetworkClients; onReport: (clientTenantId: string) => void }) {
  const { t } = useSurface();
  const rows = data.clients.flatMap((c): { key: string; client: Client; conn: Client['connections'][number] | null }[] =>
    c.connections.length ? c.connections.map((conn) => ({ key: `${c.clientTenantId}:${conn.id}`, client: c, conn })) : [{ key: c.clientTenantId, client: c, conn: null }]);
  return (
    <div data-testid={`network-connections-${provider}`}>
      <Notice testId="network-capabilities">{t('portal.partners.adsNet.clientConnects')}</Notice>
      {!data.capabilities.accountMapping ? <Text tone="muted">{t('portal.partners.adsNet.noAgencyLink')}</Text> : null}
      <DataTable rows={rows} rowKey={(r) => r.key} empty={t('portal.partners.adsNet.noClients')} columns={[
        { key: 'client', header: t('portal.partners.adsNet.client'), render: (r) => r.client.clientName ?? r.client.clientTenantId },
        { key: 'acct', header: t('portal.partners.ads.account'), render: (r) => (r.conn ? r.conn.displayName ?? r.conn.accountRef : '-') },
        {
          key: 'status', header: t('portal.partners.clients.col.status'), render: (r) => (r.conn
            ? <Badge tone={CONNECTION_TONE[r.conn.status] ?? 'neutral'} testId={`net-conn-${r.conn.status}`}>{t(`portal.partners.conn.${r.conn.status}`)}</Badge>
            : <Badge tone="neutral" testId="net-conn-none">{t('portal.partners.adsNet.notConnected')}</Badge>),
        },
        { key: 'sync', header: t('portal.partners.adsNet.lastSync'), narrow: 'hide', render: (r) => (r.conn?.lastSyncedAt ? <When iso={r.conn.lastSyncedAt} /> : '-') },
        {
          key: 'act', header: '', render: (r) => (r.conn
            ? <button type="button" className="qx-link" onClick={() => onReport(r.client.clientTenantId)}>{t('portal.partners.adsNet.viewReport')}</button>
            : null),
        },
      ]} />
    </div>
  );
}

/** Per-client spend and performance for one network over a date range. */
export function NetworkReport({ provider, clients, clientId, range, onChange }: {
  provider: AgencyNetworkProvider; clients: Client[]; clientId: string; range: { from: string; to: string };
  onChange: (next: { client?: string; from?: string; to?: string }) => void;
}) {
  const { t } = useSurface();
  const api = useNetworksApi();
  const valid = !!clientId && ISO_DAY.test(range.from) && ISO_DAY.test(range.to) && range.from <= range.to;
  return (
    <div data-testid={`network-report-${provider}`}>
      <div className="qx-toolbar">
        <Field label={t('portal.partners.adsNet.client')}>
          {(id) => (
            <select id={id} value={clientId} onChange={(e) => onChange({ client: e.target.value })}>
              <option value="">{t('portal.partners.adsNet.chooseClient')}</option>
              {clients.map((c) => <option key={c.clientTenantId} value={c.clientTenantId}>{c.clientName ?? c.clientTenantId}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('portal.partners.adsNet.from')}>{(id) => <input id={id} type="date" value={range.from} max={range.to} onChange={(e) => onChange({ from: e.target.value })} />}</Field>
        <Field label={t('portal.partners.adsNet.to')}>{(id) => <input id={id} type="date" value={range.to} min={range.from} onChange={(e) => onChange({ to: e.target.value })} />}</Field>
      </div>
      {!clientId ? <Notice testId="report-choose-client">{t('portal.partners.adsNet.chooseClientHint')}</Notice>
        : !valid ? <Notice tone="warn">{t('portal.partners.adsNet.badWindow')}</Notice>
        : <ReportBody provider={provider} clientId={clientId} range={range} load={() => api.report(provider, clientId, range)} />}
    </div>
  );
}

function ReportBody({ provider, clientId, range, load }: { provider: AgencyNetworkProvider; clientId: string; range: { from: string; to: string }; load: () => Promise<AgencyNetworkReport> }) {
  const { t } = useSurface();
  const num = useNumber();
  const report = useResource(`adsnet:${provider}:${clientId}:${range.from}:${range.to}`, load);
  return (
    <Loadable resource={report}>
      {(r) => (
        <>
          {r.freshness.stale ? <Notice tone="warn" testId="report-stale">{t('portal.partners.adsNet.stale')}</Notice> : null}
          {r.partial.isPartial ? <Notice tone="warn" testId="report-partial">{t('portal.partners.adsNet.partial', { dates: r.partial.missingDates.join(', ') })}</Notice> : null}
          {r.freshness.lastSyncedAt ? <Text tone="muted">{t('portal.partners.adsNet.lastSync')}: <When iso={r.freshness.lastSyncedAt} /></Text> : null}
          {r.connections.length === 0 ? <Notice testId="report-no-account">{t('portal.partners.adsNet.noAccount')}</Notice> : null}
          {r.connections.length > 0 && r.totals.length === 0 ? <Notice testId="report-no-data">{t('portal.partners.adsNet.noData')}</Notice> : null}
          {r.totals.map((tot) => (
            <Panel key={tot.currency} data-currency={tot.currency}>
              <Heading level={2}>{t('portal.partners.adsNet.totals', { currency: tot.currency })}</Heading>
              <Grid>
                <Stat label={t('portal.partners.adsNet.spend')} value={<Money minor={tot.spendMinor} currency={tot.currency} />} testId={`stat-spend-${tot.currency}`} />
                <Stat label={t('portal.partners.adsNet.impressions')} value={num(tot.impressions)} />
                <Stat label={t('portal.partners.adsNet.clicks')} value={num(tot.clicks)} />
                <Stat label={t('portal.partners.adsNet.ctr')} value={tot.ctr === null ? '-' : `${num(Math.round(tot.ctr * 10_000) / 100)}%`} />
                <Stat label={t('portal.partners.adsNet.cpc')} value={tot.cpcMinor === null ? '-' : <Money minor={tot.cpcMinor} currency={tot.currency} />} />
                <Stat label={t('portal.partners.adsNet.leads')} value={num(tot.leads)} />
                <Stat label={t('portal.partners.adsNet.cpl')} value={tot.cplMinor === null ? '-' : <Money minor={tot.cplMinor} currency={tot.currency} />} />
                <Stat label={t('portal.partners.adsNet.conversions')} value={num(tot.conversions)} />
              </Grid>
            </Panel>
          ))}
          {r.byCampaign.length > 0 ? (
            <DataTable rows={r.byCampaign} rowKey={(x) => `${x.connectionId}:${x.campaignRef}:${x.currency}`} empty={t('portal.kit.empty')} caption={t('portal.partners.adsNet.campaigns')} columns={[
              { key: 'campaign', header: t('portal.partners.adsNet.campaign'), render: (x) => x.campaignName ?? x.campaignRef },
              { key: 'spend', header: t('portal.partners.adsNet.spend'), render: (x) => <Money minor={x.spendMinor} currency={x.currency} /> },
              { key: 'imp', header: t('portal.partners.adsNet.impressions'), narrow: 'hide', render: (x) => num(x.impressions) },
              { key: 'clk', header: t('portal.partners.adsNet.clicks'), narrow: 'hide', render: (x) => num(x.clicks) },
              { key: 'leads', header: t('portal.partners.adsNet.leads'), render: (x) => num(x.leads) },
              { key: 'cpl', header: t('portal.partners.adsNet.cpl'), narrow: 'hide', render: (x) => (x.cplMinor === null ? '-' : <Money minor={x.cplMinor} currency={x.currency} />) },
            ]} />
          ) : null}
        </>
      )}
    </Loadable>
  );
}

/** One screen per network: /ads/tiktok, /ads/snap, /ads/linkedin. */
export function AdsNetworkPage({ provider }: { provider: AgencyNetworkProvider }) {
  const { t, now } = useSurface();
  const api = useNetworksApi();
  const [sp, setSp] = useSearchParams();
  const [fallback] = useState(() => defaultWindow(now()));
  const tab = sp.get('tab') === 'reports' ? 'reports' : 'connections';
  const range = { from: sp.get('from') ?? fallback.from, to: sp.get('to') ?? fallback.to };
  const clientId = sp.get('client') ?? '';
  const clients = useResource(`adsnet:clients:${provider}`, () => api.clients(provider));
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setSp(next);
  };
  const title = t(`portal.partners.adsNet.${provider}.title`);
  return (
    <Screen title={title} subtitle={t('portal.partners.adsNet.subtitle')} testId={`ads-${provider}`}>
      <Tabs label={title} active={tab} onSelect={(v) => set({ tab: v })}
        tabs={[{ id: 'connections', label: t('portal.partners.adsNet.tab.connections') }, { id: 'reports', label: t('portal.partners.adsNet.tab.reports') }]} />
      <Loadable resource={clients}>
        {(data) => (tab === 'reports'
          ? <NetworkReport provider={provider} clients={data.clients} clientId={clientId} range={range} onChange={(n) => set({ ...(n.client !== undefined ? { client: n.client } : {}), ...(n.from !== undefined ? { from: n.from } : {}), ...(n.to !== undefined ? { to: n.to } : {}) })} />
          : <NetworkConnections provider={provider} data={data} onReport={(id) => set({ tab: 'reports', client: id })} />)}
      </Loadable>
    </Screen>
  );
}

export const TikTokAdsPage = () => <AdsNetworkPage provider="tiktok" />;
export const SnapAdsPage = () => <AdsNetworkPage provider="snap" />;
export const LinkedInAdsPage = () => <AdsNetworkPage provider="linkedin" />;
